import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {GridSortModel} from '@mui/x-data-grid';
import {GridPaginationModel} from '@mui/x-data-grid/models/gridPaginationProps';
import {GridSortItem} from '@mui/x-data-grid/models/gridSortModel';
import {
    downloadTransactionsToCSV,
    fetchTransactions,
    getTransactionDetails,
    getTransactionFilters,
    TransactionDetailsResponse,
    TransactionFiltersResponse,
    TransactionRequest,
    TransactionSummaryResponse
} from "../../api/TransactionApi";
import {TransactionDataGrid, TransactionDetailsDialog} from "./TransactionDataGrid";
import TransactionFilters from "./TransactionFilters";
import {createFilterChangeHandler, useGridHeight} from "../../utils/DataGridUtils/DataGridUtils";
import {DEFAULT_PAGE_SIZE} from "../../AppConst";
import {csvUtil} from "../../utils/CSV/CSVUtil";


export const TransactionsComponent: React.FC = () => {
    const [rows, setRows] = useState<TransactionSummaryResponse>({
        entries: [],
        totalElements: 0,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<TransactionFiltersResponse | undefined>(undefined);
    const [request, setRequest] = useState<TransactionRequest>({
        page: 0,
        size: DEFAULT_PAGE_SIZE,
        sort: ['transactionDate,desc']
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {field: 'transactionDate', sort: 'desc'},
    ]);
    const [pageModel, setPageModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
    });
    const [details, setDetails] = useState<TransactionDetailsResponse | null>(null);
    const [open, setOpen] = useState<boolean>(false);

    const filtersRef = useRef<HTMLDivElement | null>(null);
    const gridHeight = useGridHeight(filtersRef);
    const handleFilterChange = createFilterChangeHandler(setRequest);

    const updateRequest = (sortModel: readonly GridSortItem[], pageModel: GridPaginationModel) => {
        const sortParam = sortModel.map((s) => `${s.field},${s.sort}`);
        setRequest((prev) => ({...prev, sort: sortParam, page: pageModel?.page, size: pageModel?.pageSize ?? DEFAULT_PAGE_SIZE}));
    };

    useEffect(() => {
        (async () => {
            try {
                const availableFilters = await getTransactionFilters();
                setFilters(availableFilters);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchTransactions(request);
            setRows(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        updateRequest(sortModel, pageModel);
    }, [sortModel, pageModel]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleDownload = async () => {
        setLoading(true);
        const response = await downloadTransactionsToCSV(request);
        await csvUtil(response, setLoading)
    };

    const handleRowClick = async (params: any) => {
        const id = params.row.transactionId;
        const det = await getTransactionDetails(id);
        setDetails(det);
        setOpen(true);
    };

    return (
        <Box width="100%">
            <Box ref={filtersRef}>
                <TransactionFilters filters={filters} request={request} onFilterChange={handleFilterChange}/>
            </Box>
            <Box mt={2}>
                <button type={"button"} className="btn btn-primary-light" onClick={handleDownload} disabled={loading}>
                    Download CSV
                </button>
                <TransactionDataGrid
                    rows={rows}
                    loading={loading}
                    sortModel={sortModel}
                    pageModel={pageModel}
                    onSortModelChange={(model: any) => setSortModel(model)}
                    onPaginationModelChange={setPageModel}
                    onRowClick={handleRowClick}
                    height={gridHeight}
                />
            </Box>
            <TransactionDetailsDialog open={open} onClose={() => setOpen(false)} details={details}/>
        </Box>
    );
};